import {
  AvatarProps,
  Box,
  Avatar as MuiAvatar,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { HelenaAgentIcon } from "components/icons";
import { getAvatarFallbackColors } from "./utils/avatarColor";

interface CustomAvatarProps extends AvatarProps {
  url?: string | null;
  fullname?: string | null;
  sx?: SxProps<Theme>;
  tooltip?: boolean;
  variants?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Circle (default) or rounded-square tile. `sx.borderRadius` still wins. */
  shape?: "circle" | "square";
  contentSx?: SxProps<Theme>;
  showBorder?: boolean;
  backgroundColor?: string;
  textColor?: string;
  isAgent?: boolean;
  // @depricated
  showShadow?: boolean;
}
export const Avatar = ({
  url,
  fullname = "",
  sx,
  tooltip = false,
  variants = "md",
  shape = "circle",
  contentSx,
  showBorder = false,
  backgroundColor,
  textColor,
  isAgent = false,
  // @depricated
  showShadow = false,
  ...rest
}: CustomAvatarProps) => {
  const theme = useTheme();
  // Auto-color only when there is no image AND the caller gave no explicit
  // backgroundColor. `src` (passed through `...rest`) also counts as an image.
  const hasImage = Boolean(url || rest.src);
  const fallbackColors =
    !hasImage && !backgroundColor && !isAgent && fullname
      ? getAvatarFallbackColors(fullname, theme)
      : null;
  const agentBackgroundColor = isAgent
    ? theme.palette.primitive.system.violet["100"]
    : undefined;
  const resolvedBackgroundColor =
    backgroundColor ??
    agentBackgroundColor ??
    fallbackColors?.backgroundColor ??
    theme.palette.neutral["B-015"];
  const resolvedInitialsColor = textColor ?? fallbackColors?.textColor;
  const splitFullname = fullname ? fullname.split(" ") : "";
  const getSize = (type: "size" | "font") => {
    switch (variants) {
      case "xs":
        return type === "size" ? "16px" : "6px";
      case "sm":
        return type === "size" ? "24px" : "10px";
      case "md":
        return type === "size" ? "32px" : "12px";
      case "lg":
        return type === "size" ? "40px" : "14px";
      case "xl":
        return type === "size" ? "48px" : "16px";
      default:
        break;
    }
  };
  const getAgentIconSize = () => {
    switch (variants) {
      case "sm":
        return "16px";
      case "md":
        return "24px";
      case "lg":
        return "32px";
      case "xl":
        return "40px";
      default:
        return "16px";
    }
  };
  // Square tiles keep a small radius that scales with the avatar so the corners
  // read the same at every size.
  const getBorderRadius = () => {
    if (shape === "circle") return "100px";
    switch (variants) {
      case "xs":
      case "sm":
        return "4px";
      case "xl":
        return "8px";
      default:
        return "6px";
    }
  };

  return (
    <Tooltip
      title={tooltip ? fullname : ""}
      placement="top"
      followCursor={true}
      PopperProps={{
        sx: {
          "& .MuiTooltip-tooltip": {
            backgroundColor: backgroundColor
              ? backgroundColor
              : theme.palette.neutral["B-015"],
            color: textColor ? textColor : theme.palette.lightBg.high,
            ...theme.typography["caption"],
          },
        },
      }}
    >
      <Box>
        <MuiAvatar
          alt={`${fullname}`}
          src={url ? url : undefined}
          sx={{
            borderRadius: getBorderRadius(),
            border: showBorder ? "2px solid #FFF" : "none",
            backgroundColor: resolvedBackgroundColor,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: getSize("size"),
            height: getSize("size"),
            textTransform: "uppercase",
            // boxShadow: showShadow ? "0px 2px 4px 0px #00000024" : "none",

            ...sx,
          }}
          {...rest}
        >
          {isAgent ? (
            <HelenaAgentIcon sx={{ fontSize: getAgentIconSize() }} />
          ) : (
            <Typography
              variant="button-small"
              sx={{
                fontFamily: "SegoeBold",
                fontWeight: 700,
                fontSize: getSize("font"),
                textTransform: "uppercase",
                textAlign: "center",
                color: resolvedInitialsColor,
                ...contentSx,
              }}
            >
              {splitFullname &&
              Array.isArray(splitFullname) &&
              splitFullname.length > 0
                ? `${splitFullname[0] ? splitFullname[0].charAt(0) : ""}${
                    splitFullname[1] ? splitFullname[1].charAt(0) : ""
                  }`
                : ""}
            </Typography>
          )}
        </MuiAvatar>
      </Box>
    </Tooltip>
  );
};
